import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';

import { createErrorsMapper } from '../../service/ErrorsMapper/ErrorsMapper';
import {
    ValidationErrorData,
    ValidationResultItem
} from '../../typings/common';
import {
    minusPhraseLibraryConstantsFragment,
    minusPhraseLibraryRowFragment
} from '../../typings/graphql-grid';
import { MinusPhraseLibraryRow } from '../../typings/minusPhraseLibrary';
import { MinusPhraseList } from '../../typings/state';

import * as keyset from './minus-phrase.i18n';

const i18n = i18nFactory(keyset);

export function mapValidationResultItemToErrorData(
    errors?: ValidationResultItem[]
): ValidationErrorData[] {
    if (!errors) {
        return [];
    }

    return errors.map(
        (error: ValidationResultItem): ValidationErrorData => {
            return {
                path: error.path,
                code: error.code,
                text: mapMinusPhraseValidationResultApiData(error)
            };
        }
    );
}

export function mapMinusPhraseRowApiData(
    data: minusPhraseLibraryRowFragment[]
): MinusPhraseLibraryRow[] {
    return data.map((item: minusPhraseLibraryRowFragment) => {
        return {
            id: item.id,
            name: item.name,
            minusKeywords: ['', ...item.minusKeywords].join(' -').trim(),
            groupsCount: Number(item.linkedAdGroupsCount)
        };
    });
}

export function validateMinusPhraseData(
    data: MinusPhraseList,
    context: string,
    constants: minusPhraseLibraryConstantsFragment
): ValidationResultItem[] {
    const { minusPhrases, title } = data;
    const validation: ValidationResultItem[] = [];
    const titleAvailableLetters = new RegExp(
        `[^${constants.nameAllowLetters
            .replace(/\\/g, '')
            .replace(/[.?*+^$[\]\\(){}|-]/g, '\\$&')}]`
    );
    const minusPhraseAvailableLetters = new RegExp(
        `[^${constants.textAllowLetters
            .replace(/\\/g, '')
            .replace(/[.?*+^$[\]\\(){}|-]/g, '\\$&')}]`
    );
    const formattedMinusPhrases = formatMinusPhrasesToApi(minusPhrases);

    if (!minusPhrases) {
        validation.push({
            path: `${context}[0].minusKeywords`,
            code: 'CollectionDefectIds.Gen.CANNOT_BE_EMPTY',
            params: null
        });
    }

    if (!title) {
        validation.push({
            path: `${context}[0].name`,
            code: 'StringDefectIds.CANNOT_BE_EMPTY',
            params: null
        });
    }

    if (titleAvailableLetters.test(title)) {
        validation.push({
            path: `${context}[0].name`,
            code: 'StringDefectIds.MUST_CONTAIN_ONLY_SYMBOLS',
            params: null
        });
    }

    formattedMinusPhrases.forEach((val: string, i: number) => {
        if (minusPhraseAvailableLetters.test(val)) {
            validation.push({
                path: `${context}[0].minusKeywords[${i}]`,
                code:
                    'MinusPhraseDefectIds.MinusPhrase.ILLEGAL_MINUS_KEYWORD_CHARS',
                params: {
                    subset: [val]
                }
            });
        }
    });

    if (minusPhrases.length > constants.libMinusKeywordsTextMaxLength) {
        validation.push({
            path: `${context}[0].minusKeywords`,
            code: 'MinusPhraseDefectIds.StringLength.MAX_LENGTH_MINUS_KEYWORDS',
            params: {
                maxLength: constants.libMinusKeywordsTextMaxLength
            }
        });
    }

    if (title.length > constants.maxNameLength) {
        validation.push({
            path: `${context}[0].name`,
            code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
            params: {
                maxLength: constants.maxNameLength
            }
        });
    }

    return validation;
}

export const mapMinusPhraseValidationResultApiData = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: ['addItems', idx, 'name'],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('titleEmpty')
                },
                {
                    code: 'StringDefectIds.MUST_CONTAIN_ONLY_SYMBOLS',
                    message: (): string => i18n('titleSymbols')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string =>
                        i18n('titleMax', {
                            count: get(err, 'params.maxLength', '')
                        })
                }
            ]
        },
        {
            path: ['addItems', idx],
            codes: [
                {
                    code: 'NumberDefectIds.MUST_BE_LESS_THEN_OR_EQUAL_TO_MAX',
                    message: (): string => i18n('maxLibraryLists')
                }
            ]
        },
        {
            path: ['addItems', idx, 'minusKeywords'],
            codes: [
                {
                    code:
                        'MinusPhraseDefectIds.StringLength.MAX_LENGTH_MINUS_KEYWORDS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMax', {
                            count: get(err, 'params.maxLength', '')
                        })
                },
                {
                    code: 'CollectionDefectIds.Gen.CANNOT_BE_EMPTY',
                    message: (): string => i18n('minusPhraseEmpty')
                }
            ]
        },
        {
            path: ['addItems', idx, 'minusKeywords', idx],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('minusPhraseEmpty')
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.ILLEGAL_MINUS_KEYWORD_CHARS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseCharts', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.WRONG_QUOTES',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseQuotes', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.SEPARATE_DOT',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseDots', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_WORD_FIRST_CHARACTER',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseFirstChar', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_DOT',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseInvalidDot', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.IndividualKeywordLength.MAX_COUNT_WORDS_IN_KEYWORD',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMaxWordsInMinusPhrase', {
                            subset: get(err, 'params.subset', '').join(','),
                            count: get(err, 'params.maxWords', '')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.IndividualKeywordLength.MAX_LENGTH_MINUS_WORD',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMaxLengthWordsInMinusPhrase', {
                            subset: get(err, 'params.subset', '').join(','),
                            count: get(err, 'params.maxLength', '')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.IMBALANCED_SQUARE_BRACKETS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseBrackets', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.NESTED_OR_EMPTY_SQUARE_BRACKETS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseBrackets', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_OPERATORS_INSIDE_SQUARE_BRACKETS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseBracketsOperator', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_EXCLAMATION_MARK',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseExclamation', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_MINUS_MARK',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMinus', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_PLUS_MARK',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhrasePlus', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_COMBINATION_SPECIAL_SYMBOLS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseSpecialSymbols', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                }
            ]
        },
        {
            path: ['updateItems', idx, 'name'],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('titleEmpty')
                },
                {
                    code: 'StringDefectIds.MUST_CONTAIN_ONLY_SYMBOLS',
                    message: (): string => i18n('titleSymbols')
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string =>
                        i18n('titleMax', {
                            count: get(err, 'params.maxLength', '')
                        })
                }
            ]
        },
        {
            path: ['updateItems', idx, 'minusKeywords'],
            codes: [
                {
                    code:
                        'MinusPhraseDefectIds.StringLength.MAX_LENGTH_MINUS_KEYWORDS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMax', {
                            count: get(err, 'params.maxLength', '')
                        })
                },
                {
                    code: 'CollectionDefectIds.Gen.CANNOT_BE_EMPTY',
                    message: (): string => i18n('minusPhraseEmpty')
                }
            ]
        },
        {
            path: ['updateItems', idx, 'minusKeywords', idx],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('minusPhraseEmpty')
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.ILLEGAL_MINUS_KEYWORD_CHARS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseCharts', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.WRONG_QUOTES',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseQuotes', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.SEPARATE_DOT',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseDots', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_WORD_FIRST_CHARACTER',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseFirstChar', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_DOT',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseInvalidDot', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.IndividualKeywordLength.MAX_COUNT_WORDS_IN_KEYWORD',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMaxWordsInMinusPhrase', {
                            subset: get(err, 'params.subset', '').join(','),
                            count: get(err, 'params.maxWords', '')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.IndividualKeywordLength.MAX_LENGTH_MINUS_WORD',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMaxLengthWordsInMinusPhrase', {
                            subset: get(err, 'params.subset', '').join(','),
                            count: get(err, 'params.maxLength', '')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.IMBALANCED_SQUARE_BRACKETS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseBrackets', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.NESTED_OR_EMPTY_SQUARE_BRACKETS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseBrackets', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_OPERATORS_INSIDE_SQUARE_BRACKETS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseBracketsOperator', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_EXCLAMATION_MARK',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseExclamation', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_MINUS_MARK',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseMinus', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code: 'MinusPhraseDefectIds.MinusPhrase.INVALID_PLUS_MARK',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhrasePlus', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                },
                {
                    code:
                        'MinusPhraseDefectIds.MinusPhrase.INVALID_COMBINATION_SPECIAL_SYMBOLS',
                    message: (err: ValidationResultItem): string =>
                        i18n('minusPhraseSpecialSymbols', {
                            subset: get(err, 'params.subset', '').join(',')
                        })
                }
            ]
        }
    ],
    error => i18n('textError', { path: error.path, code: error.code })
);

export function prepareMinusPhrases(minusPhrases: string): string {
    const formattedMinusPhrases = minusPhrases
        .replace(/((^|\s),+|,\s|-|\n|\r|\t)/g, ' -')
        .replace(/(\s+)/g, ' ')
        // убираем пробелы справа от !, +, -
        .replace(/(!|\+|-)\s+/g, function(match: string, ch: string): string {
            return ch;
        })
        // удаляем - в начале, в конце фразы
        .replace(/(^-|-$)/g, '')
        // убираем лишние пробелы между []
        .replace(/\[(\s*.+\s*)\]/g, function(
            match: string,
            text: string
        ): string {
            return '[' + text.trim() + ']';
        })
        // удаляем ---
        .replace(/(-+)/g, '-')
        // убираем лишние пробелы с двух сторон от "
        .replace(/\s*"\s*/g, '"')
        .trim();

    if (
        formattedMinusPhrases.length &&
        !formattedMinusPhrases.startsWith('-')
    ) {
        return '-' + formattedMinusPhrases;
    } else {
        return formattedMinusPhrases;
    }
}

export function formatMinusPhrasesToApi(minusPhrases: string): Array<string> {
    return minusPhrases.split('-').filter(Boolean);
}
